<template>
  <div>
    <ai-tabs
      v-if="filters.length > 1"
      v-model="currentFilter"
      :items="filters" />

    <div class="BookingFilters-result">
      <ai-typo
        v-if="!loading"
        as="p"
        variant="paragraph-03-regular"
        color="stratos-700"
        aria-live="polite">
        <ai-typo
          v-if="resultCount"
          as="strong"
          variant="paragraph-03-bold"
          color="stratos-900">
          {{ resultCount }}
        </ai-typo>
        {{ $t('booking.filters.numberResults', { count: resultCount }) }}
      </ai-typo>
      <ai-skeleton
        v-else
        desktop-width="160px"
        desktop-height="18px"
        mobile-width="160px"
        mobile-height="18px" />

      <div class="BookingFilters-result--currency">
        <ai-typo as="span" variant="paragraph-03-regular" color="stratos-800">
          {{ $t('booking.filters.ratesSelection') }}
        </ai-typo>

        <ai-field-select
          v-model="currentCurrency"
          :label="$t('booking.input.label.currency')"
          :option-label="$t('booking.filters.ratesSelection')"
          :options="currencyOptions" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BookingFilterValues } from '../../interfaces';
import { useBookingStore } from '../../store';

import { useCurrenciesAsyncData } from '~~/domains/graphql';
import type { AiInputOption } from '~~/domains/ux/molecules/AiForm/interfaces';

type Props = {
  filters: BookingFilterValues[];
  modelValue?: string;
  resultCount: number;
  loading: boolean;
};

type Emits = {
  (event: 'update:modelValue', value: string | number | undefined): void;
};

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const bookingStore = useBookingStore();
const currencies = await useCurrenciesAsyncData();

const currencyOptions = computed<AiInputOption[]>(() => {
  if (!currencies?.data?.value?.currencies?.length) {
    return [];
  }

  return currencies.data.value.currencies
    .map(currency => ({
      label: currency.code,
      value: currency.code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

const currentCurrency = computed({
  get() {
    return bookingStore.currentCurrency ?? '';
  },
  set(value) {
    bookingStore.setCurrency(value);
  },
});

const currentFilter = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/colors';

.BookingFilters-tabs {
  margin-bottom: constants.$margin-04;
}

.BookingFilters-result {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: constants.$margin-04;

  &--currency {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: constants.$margin-01;

    & > span {
      @media (mq.$small) {
        display: none;
      }
    }

    & :deep(select) {
      color: colors.$stratos-700;
      font-weight: 400;
    }

    & :deep(label) {
      color: colors.$stratos-900;
    }
  }
}
</style>
